import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAPIUpload, usePost } from "./API";
import { categorizeSignedURLs, snakeToEnglish, fileNameFromUrl } from "./Util";
import { DataTable, Modal, Badge, Spinner, useToast, Select, Alert, Button, TextArea, Tooltip, Icon, SelectOptionType, Timeline, Input, ActionsDropdown } from "@aidkitorg/component-library";
import { TableAction } from "@aidkitorg/component-library/dist/types/src/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import { FieldConfiguration } from "@aidkitorg/types/lib/admin-panel/settings";
import { AuditLog } from "@aidkitorg/aidkit/lib/admin_panel/audit_logs/repository/IAuditLogRepository";
import { TurnableImage } from "./Components/TurnableImage";
import { Attachment } from "./Questions/Attachment";
import { useLocalizedStrings } from "./Localization";
import { buildFileHistory, FileHubDocument, lastModifiedByFile } from "./fileHubHistory";

function renderMediaPreview(params: {
  imageUrls: string[],
  videoUrl: string | null,
  audioUrl: string | null,
  alt?: string,
  noPreviewText: string,
}) {
  const { imageUrls, videoUrl, audioUrl, alt = '', noPreviewText } = params;
  const hasMedia = imageUrls.length > 0 || videoUrl || audioUrl;

  return (
    <div className="overflow-auto my-4">
      {videoUrl && (
        <video className="w-full rounded" controls crossOrigin="anonymous" src={videoUrl} />
      )}
      {audioUrl && (
        <audio className="w-full" controls preload="auto">
          <source src={audioUrl} type="audio/mpeg" />
        </audio>
      )}
      {imageUrls.length > 0 && (
        <TurnableImage Viewer='screener' alt={alt} srcs={imageUrls} fullScreenUrl={imageUrls[0]} />
      )}
      {!hasMedia && (
        <p className="text-gray-500 py-8 text-center">{noPreviewText}</p>
      )}
    </div>
  );
}

function FileHistory(props: { entries: (AuditLog | FileHubDocument)[] }) {
  const L = useLocalizedStrings();
  const FH = (L.applicant as any).file_hub;

  const history = buildFileHistory(props.entries);

  if (history.length === 0) {
    return <p className="text-gray-500 py-8 text-center">{FH.no_history_available}</p>;
  }

  return (
    <Timeline>
      {history.map((entry) => {
        const key = entry.bucket;
        const time = new Date(entry.timestamp).toISOString();

        if (entry.action) {
          const label = entry.action === 'move_to'
            ? <>{FH.moved_from} <code className="text-black">{entry.sourceField}</code> {FH.moved_to} <code className="text-black">{entry.targetField}</code></>
            : entry.action === 'copy_to'
              ? <>{FH.moved_to} <code className="text-black">{entry.targetField}</code></>
              : <>{FH.removed_from} <code className="text-black">{entry.targetField}</code></>;

          return (
            <Timeline.Item key={`action-${key}`} time={time} variant="primary">
              <div>{label} {FH.by} {entry.source}</div>
              {entry.note && <div className="mt-1 text-gray-500 text-sm">{FH.note_label} "{entry.note}"</div>}
            </Timeline.Item>
          );
        }

        if (entry.renamedTo) {
          return (
            <Timeline.Item key={`rename-${key}`} time={time} variant="primary">
              <div>{FH.renamed_to} <span className="text-black font-medium">"{entry.renamedTo}"</span> {FH.by} {entry.source}</div>
            </Timeline.Item>
          );
        }

        const targetFieldEl = entry.targetField
          ? <> {FH.to_field} <code className="text-black">{entry.targetField}</code></>
          : null;

        const uploadedBy = entry.source === 'Comms'
          ? <>{FH.uploaded_via_comms}{targetFieldEl}</>
          : !entry.source || entry.source.toLowerCase() === 'system'
            ? <>{FH.uploaded}{targetFieldEl} {FH.uploaded_by_applicant}</>
            : <>{FH.uploaded}{targetFieldEl} {FH.by} {entry.source}</>;

        return (
          <Timeline.Item key={`doc-${key}`} time={time} variant="primary">
            <span>{uploadedBy}</span>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}

// Renders the little pill shown next to each copy-to target field indicating how many
// documents currently live on that field: a blue "N doc(s)" pill when there are some,
// or a grey "--" pill when the field is empty.
function renderFieldDocumentCountPill(count: number, FH: { doc: string; docs: string }) {
  if (count > 0) {
    return (
      <Badge variant="info" style="pill" className="text-xs px-2 py-0.5 shrink-0">
        {count} {count === 1 ? FH.doc : FH.docs}
      </Badge>
    );
  }
  return (
    <Badge variant="neutral" style="pill" className="text-xs px-2 py-0.5 shrink-0">
      --
    </Badge>
  );
}

type AttachmentActionModalProps = {
  open: boolean,
  onClose: () => void,
  applicant: string,
  fileUrl: string,
  displayName?: string,
  onComplete?: () => void,
  fixedTargetField?: string,
} & (
  | { kind: 'remove', targetField: string, documentTargetFields?: never, fieldDocumentCounts?: never }
  | { kind: 'copy_to', targetField?: never, documentTargetFields: Record<string, Partial<FieldConfiguration>>, fieldDocumentCounts?: Record<string, number>, sourceTargetField?: string, initialRemoveFromSource?: boolean }
);

export function AttachmentActionModal(props: AttachmentActionModalProps) {
  const L = useLocalizedStrings();
  const FH = (L.applicant as any).file_hub;
  const postMoveDocument = usePost("/applicant/move_document");
  const getSignedURL = usePost("/v2/document/view");
  const [explanation, setExplanation] = useState("");
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTargetField, setSelectedTargetField] = useState<SelectOptionType | undefined>(undefined);

  const [postingMoveDocument, setPostingMoveDocument] = useState(false);

  useEffect(() => {
    if (!props.open || !props.fileUrl) return;
    setExplanation("");
    if (props.kind === 'copy_to' && props.fixedTargetField && props.documentTargetFields[props.fixedTargetField]) {
      const tf = props.fixedTargetField;
      const config = props.documentTargetFields[tf];
      setSelectedTargetField({
        label: config.displayName || snakeToEnglish(tf),
        value: tf,
      });
    } else {
      setSelectedTargetField(undefined);
    }
    setImageUrls([]);
    setVideoUrl(null);
    setAudioUrl(null);
    setLoading(true);
    (async () => {
      try {
        const result = await getSignedURL({ paths: [props.fileUrl] });
        if (result?.paths?.[props.fileUrl]) {
          const { imageFileUrls, videoFileUrls, audioFileUrls } = categorizeSignedURLs(result.paths[props.fileUrl]);
          setImageUrls(imageFileUrls);
          setVideoUrl(videoFileUrls[0] ?? null);
          setAudioUrl(audioFileUrls[0] ?? null);
        }
      } catch (error) {
        console.error("Failed to fetch signed URL for preview:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [props.open, props.fileUrl]);

  const isMove = props.kind === 'copy_to' && (props.initialRemoveFromSource ?? false);

  const targetFieldOptions = useMemo<SelectOptionType[]>(() => {
    if (props.kind !== 'copy_to') return [];
    // Only render the doc-count pills when counts are supplied (File Hub). Other
    // callers keep the plain display-name labels rather than showing empty pills.
    const counts = props.fieldDocumentCounts;
    return Object.entries(props.documentTargetFields).filter(([key]) => !isMove || key !== props.sourceTargetField).map(([key, config]) => {
      const displayName = config.displayName || snakeToEnglish(key);
      return {
        label: counts
          ? (
            <span className="flex items-center justify-between gap-2 w-full">
              <span className="truncate">{displayName}</span>
              {renderFieldDocumentCountPill(counts[key] ?? 0, FH)}
            </span>
          )
          : displayName,
        value: key,
      };
    });
  }, [
    props.kind === 'copy_to' ? props.documentTargetFields : null,
    props.kind === 'copy_to' ? props.fieldDocumentCounts : null,
    props.kind === 'copy_to' ? props.sourceTargetField : null,
    props.kind === 'copy_to' ? props.initialRemoveFromSource : null,
  ]);

  const selectedFieldDescription = props.kind === 'copy_to' && selectedTargetField
    ? props.documentTargetFields[selectedTargetField.value]?.description
    : undefined;

  const resolvedTargetField = props.kind === 'remove' ? props.targetField : (props.fixedTargetField || selectedTargetField?.value);

  const isSubmitDisabled = props.kind === 'copy_to' && !props.fixedTargetField && !selectedTargetField;

  const handleSubmit = async () => {
    if (!resolvedTargetField) return;
    setPostingMoveDocument(true);
    const result = await postMoveDocument({
      applicant: props.applicant,
      target_field: resolvedTargetField,
      update: isMove && !!props.sourceTargetField
        ? { kind: 'move_to', url: props.fileUrl, note: explanation, source_field: props.sourceTargetField }
        : { kind: props.kind, url: props.fileUrl, note: explanation },
    });
    if (result?.error) {
      setPostingMoveDocument(false);
      // error will be toasted, leave modal open.
      return;
    }
    setPostingMoveDocument(false);
    props.onClose();
    props.onComplete?.();
  };

  const fileName = props.displayName || fileNameFromUrl(props.fileUrl);

  const preview = loading
    ? <p className="text-gray-500 py-8 text-center">{FH.loading_preview}</p>
    : renderMediaPreview({ imageUrls, videoUrl, audioUrl, alt: fileName, noPreviewText: FH.unable_to_load_file_preview });

  if (props.kind === 'remove') {
    return (
      <Modal open={props.open} onClose={() => props.onClose()} title={FH.remove_from_attachment} size="md">
        <div className="flex flex-col gap-4">
          {fileName && <p className="text-base font-semibold text-gray-900 break-all">{fileName}</p>}
          {preview}
          <p className="text-sm text-gray-700">
            {FH.remove_body_prefix} <strong>{snakeToEnglish(props.targetField)}</strong>.{" "}
            {FH.remove_body_suffix}
          </p>
          <TextArea
            label={FH.reason_for_removal}
            placeholder={FH.reason_for_removal_placeholder}
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            rows={3}
          />
          <Button variant="secondary" onClick={handleSubmit} isDisabled={isSubmitDisabled} loading={postingMoveDocument}>
            {FH.remove}
          </Button>
        </div>
      </Modal>
    );
  }

  const modalTitle = props.fixedTargetField ? FH.copy_to_current_attachment : (isMove ? FH.move_document_to : FH.copy_document_to);

  return (
    <Modal open={props.open} onClose={() => props.onClose()} title={modalTitle} size="md">
      <div className="flex flex-col gap-4">
        {fileName && <p className="text-base font-semibold text-gray-900 break-all">{fileName}</p>}
        {!props.fixedTargetField && (isMove
          ? <div className="mt-2 rounded-md px-3 py-2 text-sm bg-yellow-50 text-yellow-800 border border-yellow-200">{FH.move_info_text}</div>
          : <div className="mt-2">{FH.copy_info_text}</div>
        )}
        {preview}
        {!props.fixedTargetField && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{isMove ? FH.move_to : FH.copy_to}</span>
              <div className="flex-1">
                <Select
                  label={FH.location}
                  hideLabel
                  description=""
                  options={targetFieldOptions}
                  value={selectedTargetField}
                  onChange={(value) => setSelectedTargetField(value)}
                  searchable
                  filterFunction={(option, query) => {
                    const q = query.trim().toLowerCase();
                    if (!q) return true;
                    const displayName = props.documentTargetFields[option.value]?.displayName || snakeToEnglish(option.value);
                    return displayName.toLowerCase().includes(q) || option.value.toLowerCase().includes(q);
                  }}
                  customButtonTextRenderer={props.fieldDocumentCounts
                    ? (key) => key ? (props.documentTargetFields[key]?.displayName || snakeToEnglish(key)) : ''
                    : undefined}
                  clearable
                />
              </div>
            </div>
            {selectedFieldDescription && selectedTargetField && (
              <Alert variant="primary" className="p-2">
                <div>{FH.field_label} {selectedTargetField.value}</div>
                <div>{FH.description_label} {selectedFieldDescription}</div>
              </Alert>
            )}
          </>
        )}
        <TextArea
          label={FH.reason_for_copy}
          placeholder={FH.reason_for_copy_placeholder}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          rows={3}
        />
        <Button variant="primary" onClick={handleSubmit} isDisabled={isSubmitDisabled} loading={postingMoveDocument}>
          {isMove ? FH.move : FH.copy}
        </Button>
      </div>
    </Modal>
  );
}

export function UploadFile(props: { applicant: string, onUploadComplete?: () => void }) {
  const L = useLocalizedStrings();
  const FH = (L.applicant as any).file_hub;
  const getUploadURL = usePost("/document/upload_url");
  const cleanUpload = usePost("/document/clean_upload", { suppressErrorDisplay: true });
  const upload = useAPIUpload("/upload", () => {});
  const moveDocument = usePost("/applicant/move_document");
  const { toast } = useToast();

  const [progress, setProgress] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    for (const file of fileArray) {
      if (file.size > 10485760) {
        toast({ description: FH.file_too_large.replace("$file", file.name), variant: 'error' });
        continue;
      }

      try {
        setProgress('0%');
        const url = await getUploadURL({ path: file.name, length: file.size });

        await upload([file] as any, {}, url.uploadURL, "PUT", (p: number) => {
          setProgress(`${Math.round(p * 100)}%`);
        });



        let uploadSucceeded = false;
        if (url.savedPath) {
          const cleanResult = await cleanUpload({ savedPath: url.savedPath });
          if (cleanResult.savedPath) {
            const moveResult = await moveDocument({
              applicant: props.applicant,
              target_field: 'file_hub_attachment',
              update: { kind: 'copy_to', url: cleanResult.savedPath, note: FH.uploaded_directly_note },
            });
            if (moveResult?.error) {
              toast({ description: FH.could_not_move_document, variant: 'error' });
              continue;
            }
            uploadSucceeded = true;
          }
        }
        if (!uploadSucceeded) {
          toast({ description: FH.failed_to_upload.replace("$file", file.name), variant: 'error' });
          continue;
        }
      } catch (e) {
        console.error("Upload failed:", e);
        toast({ description: FH.failed_to_upload.replace("$file", file.name), variant: 'error' });
      } finally {
        setProgress(null);
      }
    }

    props.onUploadComplete?.();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [getUploadURL, cleanUpload, upload, props.onUploadComplete, props.applicant, L, toast, moveDocument]);

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragging(false);
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
        dragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
      }`}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => { if (e.target.files) handleFiles(e.target.files); }}
      />
      {progress ? (
        <div className="flex flex-col items-center gap-2">
          <Spinner size="small" />
          <span className="text-sm text-gray-600">{FH.uploading} {progress}</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1">
          <Icon name="ArrowUpTrayIcon" className="w-8 h-8 text-gray-400" />
          <span className="text-sm text-gray-600">{FH.drag_drop_or_browse}</span>
        </div>
      )}
    </div>
  );
}

export function FileHub(props: { applicant: string }) {
  const L = useLocalizedStrings();
  const FH = L.applicant.file_hub;
  const DT = L.component_library.data_table;
  const getDocuments = usePost("/applicant/get_documents");
  const getDocumentTargetFields = usePost("/applicant/get_document_target_fields");
  const getSignedURL = usePost("/v2/document/view");
  const getAuditLogs = usePost("/api/admin_panel/user_audit_log");
  const postRenameDocument = usePost("/applicant/rename_document");

  // Document target fields are specified in program config as target fields that contain documents.
  const [documentTargetFields, setDocumentTargetFields] = useState<Record<string, Partial<FieldConfiguration>>>({});
  const [documents, setDocuments] = useState<FileHubDocument[]>([]);
  const [documentHistory, setDocumentHistory] = useState<Record<string, (AuditLog | FileHubDocument)[]>>({});
  const [displayNames, setDisplayNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const [previewImageUrls, setPreviewImageUrls] = useState<string[]>([]);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeFileUrl, setActiveFileUrl] = useState<string | null>(null);

  const { toast } = useToast();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const [copyToModalOpen, setCopyToModalOpen] = useState(false);
  const [copyToSourceField, setCopyToSourceField] = useState<string | null>(null);
  const [copyToInitialMove, setCopyToInitialMove] = useState(false);

  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeTargetField, setRemoveTargetField] = useState<string | null>(null);

  // 'list' is the default (DataTable) view; 'grid' renders each document as an Attachment card.
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [search, setSearch] = useState('');
  // Grid view is paginated separately from the DataTable. Page size is chosen in groups
  // of 8 (the grid is 4 columns wide, so each step of 8 adds two rows).
  const [gridPageSize, setGridPageSize] = useState(8);
  const [gridPage, setGridPage] = useState(0);

  // Reset to the first grid page whenever the result set or page size changes.
  useEffect(() => {
    setGridPage(0);
  }, [search, gridPageSize]);

  const fetchDocumentsAndHistory = useCallback(async () => {
    const [docsResult, auditLogsResult] = await Promise.all([
      getDocuments({ applicant: props.applicant }),
      getAuditLogs({
        limit: 100,
        filterConfig: {
          enabled: true,
          config: {
            logic: 'and',
            conditions: [
              { field: 'applicant', operator: 'equals', value: props.applicant },
              {
                logic: 'or',
                conditions: [
                  { field: 'event', operator: 'equals', value: 'APPLICANT_DOCUMENT_MOVED' },
                  { field: 'event', operator: 'equals', value: 'APPLICANT_DOCUMENT_RENAMED' },
                ],
              },
            ],
          },
        },
      }),
    ]);

    let history: Record<string, (AuditLog | FileHubDocument)[]> = docsResult.uniqueFiles ?? {};
    for (const log of auditLogsResult?.data?.auditLogs?.data ?? []) {
      if (log.value?.url) {
        history[log.value.url]?.push(log);
      }
    }

    setDocumentHistory(history);
    setDocuments(docsResult?.documents ?? []);
    setDisplayNames(docsResult?.displayNames ?? {});
  }, [props.applicant]);

  useEffect(() => {
    if (!props.applicant) return;
    (async () => {
      try {
        setLoading(true);
        const [, targetFieldsResult] = await Promise.all([
          fetchDocumentsAndHistory(),
          getDocumentTargetFields({}),
        ]);
  
        setDocumentTargetFields(targetFieldsResult?.documentTargetFields ?? {});
      } catch (e) {
        console.error("Failed to fetch documents:", e);
        toast({ description: FH.failed_to_fetch_documents.replace("$error", String(e)), variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [props.applicant]);

  const loadPreview = useCallback(async (url: string) => {
    setActiveFileUrl(url);
    setPreviewImageUrls([]);
    setPreviewVideoUrl(null);
    setPreviewAudioUrl(null);
    setPreviewLoading(true);
    try {
      const result = await getSignedURL({ paths: [url] });
      if (result?.paths?.[url]) {
        const { imageFileUrls, videoFileUrls, audioFileUrls } = categorizeSignedURLs(result.paths[url]);
        setPreviewImageUrls(imageFileUrls);
        setPreviewVideoUrl(videoFileUrls[0] ?? null);
        setPreviewAudioUrl(audioFileUrls[0] ?? null);
      }
    } catch (error) {
      console.error("Failed to fetch signed URL:", error);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // Number of documents currently live on each target field, keyed by field.
  // A document appears in `documents` at each field where it is currently the latest
  // value, so counting latest entries per target field gives the live doc count.
  const fieldDocumentCounts = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    for (const doc of documents) {
      if (doc.latest && doc.target_field) {
        counts[doc.target_field] = (counts[doc.target_field] ?? 0) + 1;
      }
    }
    return counts;
  }, [documents]);

  // Use the history to build last modified - we want a per-file view of this, not 
  // a per-target field view.
  const lastModified = useMemo(() => lastModifiedByFile(documentHistory), [documentHistory]);

  // Search is owned by FileHub (rather than the DataTable) so the same query filters
  // both the list and grid views. Matches the file name and location.
  // Sorted by per-file last modified (newest first) so the order matches the
  // "Last modified" column. The backend order follows applicantinfo row created_at,
  // which bumps every file in a field whenever one file is moved/copied into or out of it.
  const filteredDocuments = useMemo<FileHubDocument[]>(() => {
    const query = search.trim().toLowerCase();
    const matched = !query ? documents : documents.filter((doc) => {
      const name = (displayNames[doc.url] ?? fileNameFromUrl(doc.url)).toLowerCase();
      const location = doc.target_field
        ? (documentTargetFields[doc.target_field]?.displayName ?? snakeToEnglish(doc.target_field)).toLowerCase()
        : '';
      return name.includes(query) || location.includes(query);
    });
    const timestamp = (doc: FileHubDocument) => lastModified[doc.url]?.timestamp ?? new Date(doc.created_at).getTime();
    return [...matched].sort((a, b) => timestamp(b) - timestamp(a));
  }, [documents, search, displayNames, documentTargetFields, lastModified]);

  // Grid pagination is derived so it self-corrects when the filtered set shrinks
  // (e.g. the current page no longer exists after a search).
  const gridPageCount = Math.max(1, Math.ceil(filteredDocuments.length / gridPageSize));
  const currentGridPage = Math.min(gridPage, gridPageCount - 1);
  const gridStart = currentGridPage * gridPageSize;
  const pagedGridDocuments = filteredDocuments.slice(gridStart, gridStart + gridPageSize);
  const gridFrom = filteredDocuments.length === 0 ? 0 : gridStart + 1;
  const gridTo = Math.min(filteredDocuments.length, gridStart + gridPageSize);
  const gridPageSizeOptions = [8, 16, 24, 32];

  const columns = useMemo<ColumnDef<FileHubDocument, any>[]>(() => [
    {
      id: "file",
      header: FH.file_column,
      accessorFn: (row) => displayNames[row.url] ?? fileNameFromUrl(row.url),
      cell: ({ row }) => (
        <button
          onClick={() => { loadPreview(row.original.url); setViewModalOpen(true); }}
          className="text-indigo-600 hover:text-indigo-800 underline break-all text-left cursor-pointer bg-transparent border-none p-0"
        >
          {displayNames[row.original.url] ?? fileNameFromUrl(row.original.url)}
        </button>
      ),
    },
    {
      id: "target_field",
      header: FH.location_column,
      accessorFn: (row) => row.target_field ? snakeToEnglish(row.target_field) : "--",
      cell: ({ row }) => row.original.target_field && row.original.target_field !== 'file_hub_attachment' ? <div className="flex items-center gap-2">
        <Tooltip 
          variant="light"
          text={<div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2">
              {documentTargetFields[row.original.target_field]?.displayName ? <span>{documentTargetFields[row.original.target_field]?.displayName}:</span> : <code className="text-black">{row.original.target_field}:</code>}
              {documentTargetFields[row.original.target_field]?.description ? <div className="text-gray-500 text-sm">{documentTargetFields[row.original.target_field]?.description}</div> : <div className="text-gray-500 text-sm">{FH.field_lives_here}</div>}
            </div>
            <div>
              <span>{FH.document_currently_live}</span>
            </div>
          </div>}
        >
          <Icon name="InformationCircleIcon" className="w-4 h-4" />
        </Tooltip>
        {documentTargetFields[row.original.target_field]?.displayName ? <span>{documentTargetFields[row.original.target_field]?.displayName}</span> : <code className="text-black">{row.original.target_field}</code>}
        {row.original.latest ? <Badge variant="success">{FH.current_badge}</Badge> : <></>}
      </div> : "--",
    },
    {
      id: "last_modified_by",
      header: FH.last_modified_by_column,
      accessorFn: (row) => lastModified[row.url]?.source ?? row.source,
      cell: ({ row }) => {
        const source = lastModified[row.original.url]?.source ?? row.original.source;
        // Applicant-submitted rows are written by 'system' and have no user to join a name
        // from, so both 'system' and a blank author mean the applicant did it.
        return <span>{!source || source.toLowerCase() === 'system' ? FH.applicant_source : source}</span>;
      },
    },
    {
      id: "created_at",
      header: FH.last_modified_column,
      accessorFn: (row) => lastModified[row.url]?.timestamp ?? new Date(row.created_at).getTime(),
      cell: ({ row }) => {
        const date = new Date(lastModified[row.original.url]?.timestamp ?? row.original.created_at);
        return isNaN(date.getTime()) ? row.original.created_at : date.toLocaleString();
      },
    }
  ], [loadPreview, documentTargetFields, displayNames, lastModified, L]);

  const actions = useMemo<TableAction<FileHubDocument>[]>(() => [
    {
      label: FH.view_file,
      onClick: (row: FileHubDocument) => { loadPreview(row.url); setViewModalOpen(true); },
      icon: 'EyeIcon',
    },
    {
      label: FH.rename_prefix,
      onClick: (row: FileHubDocument) => {
        loadPreview(row.url);
        setRenameValue(displayNames[row.url] ?? fileNameFromUrl(row.url));
        setRenameModalOpen(true);
      },
      icon: 'PencilIcon',
    },
    {
      label: FH.copy_to_action,
      onClick: (row: FileHubDocument) => {
        setActiveFileUrl(row.url);
        setCopyToSourceField(row.target_field);
        setCopyToInitialMove(false);
        setCopyToModalOpen(true);
      },
      icon: 'DocumentDuplicateIcon',
    },
    {
      label: FH.move_to_action,
      onClick: (row: FileHubDocument) => {
        setActiveFileUrl(row.url);
        setCopyToSourceField(row.target_field);
        setCopyToInitialMove(true);
        setCopyToModalOpen(true);
      },
      icon: 'ArrowTopRightOnSquareIcon',
      hideWhen: (row: FileHubDocument) => !row.latest || !row.target_field || row.target_field === 'file_hub_attachment',
    },
    {
      label: FH.history,
      onClick: (row: FileHubDocument) => {
        setHistoryModalOpen(true);
        loadPreview(row.url);
      },
      icon: 'ClockIcon',
    },
    {
      label: FH.remove_from_attachment,
      onClick: (row: FileHubDocument) => {
        setActiveFileUrl(row.url);
        setRemoveTargetField(row.target_field);
        setRemoveModalOpen(true);
      },
      icon: 'XMarkIcon',
      hideWhen: (row: FileHubDocument) => !row.latest || row.target_field === 'file_hub_attachment'
    },
  ], [loadPreview, documents, displayNames, L]);

  const activeFileName = activeFileUrl ? (displayNames[activeFileUrl] ?? fileNameFromUrl(activeFileUrl)) : '';

  if (loading) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">{FH.title}</h1>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">{FH.title}</h1>
      <div className="mb-4">
        <UploadFile applicant={props.applicant} onUploadComplete={() => {
          fetchDocumentsAndHistory();
        }} />
      </div>
      {documents.length === 0 ? (
        <p className="text-gray-500">{FH.no_documents_found_for_applicant}</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex-1 max-w-sm">
              <Input
                type="search"
                label={DT.search}
                showLabel={false}
                placeholder={DT.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="secondary"
                iconPlacement="none"
                icon={<Icon name="Bars3Icon" />}
                aria-label={'List view'}
                aria-pressed={viewMode === 'list'}
                className={viewMode === 'list' ? '!bg-[var(--ak-identity-color-blue-50)] !text-[var(--ak-identity-color-blue-4)] ring-2 ring-inset ring-[var(--ak-identity-color-blue-2)]' : ''}
                onClick={() => setViewMode('list')}
              />
              <Button
                variant="secondary"
                iconPlacement="none"
                icon={<Icon name="Squares2X2Icon" />}
                aria-label={'Grid view'}
                aria-pressed={viewMode === 'grid'}
                className={viewMode === 'grid' ? '!bg-[var(--ak-identity-color-blue-50)] !text-[var(--ak-identity-color-blue-4)] ring-2 ring-inset ring-[var(--ak-identity-color-blue-2)]' : ''}
                onClick={() => setViewMode('grid')}
              />
            </div>
          </div>
          {viewMode === 'list' ? (
            <DataTable
              columns={columns}
              data={filteredDocuments}
              actions={actions}
              getRowId={(row) => `${row.target_field}-${row.url}`}
              showPagination={true}
              enableSearch={false}
            />
          ) : filteredDocuments.length === 0 ? (
            <p className="text-gray-500">{FH.no_documents_found_for_applicant}</p>
          ) : (
            <>
              {/*
                Force every thumbnail to a uniform height so the grid lines up at a glance:
                the first page image fills the card width, is cropped to a fixed height
                (top-aligned, so overflow is trimmed from the bottom), and any additional
                pages of a multi-page document are hidden.
              */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 [&_img]:block [&_img]:h-48 [&_img]:w-full [&_img]:object-cover [&_img]:object-top [&_img:not(:first-of-type)]:hidden">
                {pagedGridDocuments.map((doc) => {
                  const name = displayNames[doc.url] ?? fileNameFromUrl(doc.url);
                  const isRealField = doc.target_field && doc.target_field !== 'file_hub_attachment';
                  const locationLabel = isRealField
                    ? (documentTargetFields[doc.target_field!]?.displayName ?? snakeToEnglish(doc.target_field!))
                    : '--';
                  return (
                    <Attachment
                      key={`${doc.target_field}-${doc.url}`}
                      url={doc.url}
                      applicant={props.applicant}
                      targetField={doc.target_field ?? undefined}
                      asDiv
                      style={{ maxWidth: '100%' }}
                      headerTitle={
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900 truncate" title={name}>{name}</span>
                          <span className="text-xs font-normal text-gray-500 truncate" title={locationLabel}>{locationLabel}</span>
                          {isRealField && doc.latest && (
                            <span className="self-start"><Badge variant="success">{FH.current_badge}</Badge></span>
                          )}
                        </div>
                      }
                      actionButton={<ActionsDropdown actions={actions} item={doc} />}
                    />
                  );
                })}
              </div>
              <div className="py-2 flex flex-wrap sm:flex-nowrap gap-y-2 sm:gap-x-2 items-center sm:justify-between lg:justify-normal">
                <div className="w-1/2 sm:w-auto lg:flex-1">
                  <div className="w-full sm:w-fit">
                    <Select
                      description={L.component_library.data_table.select_page_size}
                      label={L.component_library.data_table.show}
                      labelLocation="inline"
                      value={gridPageSizeOptions
                        .map((n) => ({ label: String(n), value: String(n) }))
                        .find((opt) => opt.value === String(gridPageSize))}
                      onChange={(value) => {
                        setGridPageSize(Number(value.value));
                        setGridPage(0);
                      }}
                      options={gridPageSizeOptions.map((n) => ({ label: String(n), value: String(n) }))}
                    />
                  </div>
                </div>
                <div className="w-1/2 sm:w-auto lg:flex-1 text-sm text-gray-600 flex justify-end sm:justify-center">
                  <span>
                    {gridFrom}–{gridTo} {L.component_library.data_table.of} {filteredDocuments.length}
                  </span>
                </div>
                <div className="w-full flex sm:flex-row flex-nowrap items-center justify-between gap-x-2 lg:flex-1 sm:justify-end sm:w-auto">
                  <div className="flex gap-x-1">
                    <Tooltip asChild text={L.component_library.data_table.first_page}>
                      <Button onClick={() => setGridPage(0)} isDisabled={currentGridPage === 0} size="sm" aria-label="First" variant="secondary" icon={<Icon size="sm" type="solid" name="ChevronDoubleLeftIcon" role="decorative" />} />
                    </Tooltip>
                    <Tooltip asChild text={L.component_library.data_table.previous_page}>
                      <Button onClick={() => setGridPage(currentGridPage - 1)} isDisabled={currentGridPage === 0} size="sm" aria-label="Previous" variant="secondary" icon={<Icon size="sm" type="solid" name="ChevronLeftIcon" role="decorative" />} />
                    </Tooltip>
                  </div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">
                    {L.component_library.data_table.page} {currentGridPage + 1} {L.component_library.data_table.of} {gridPageCount}
                  </div>
                  <div className="flex gap-x-1">
                    <Tooltip asChild text={L.component_library.data_table.next_page}>
                      <Button onClick={() => setGridPage(currentGridPage + 1)} isDisabled={currentGridPage >= gridPageCount - 1} size="sm" aria-label="Next" variant="secondary" icon={<Icon size="sm" type="solid" name="ChevronRightIcon" role="decorative" />} />
                    </Tooltip>
                    <Tooltip asChild text={L.component_library.data_table.last_page}>
                      <Button onClick={() => setGridPage(gridPageCount - 1)} isDisabled={currentGridPage >= gridPageCount - 1} size="sm" aria-label="Last" variant="secondary" icon={<Icon size="sm" type="solid" name="ChevronDoubleRightIcon" role="decorative" />} />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      <Modal
        open={viewModalOpen}
        onClose={setViewModalOpen}
        title={activeFileName}
        size="lg"
      >
        {previewLoading ? (
          <p className="text-gray-500 py-8 text-center">{FH.loading_file}</p>
        ) : (
          renderMediaPreview({ imageUrls: previewImageUrls, videoUrl: previewVideoUrl, audioUrl: previewAudioUrl, alt: activeFileName, noPreviewText: FH.unable_to_load_file_preview })
        )}
      </Modal>

      <Modal
        open={historyModalOpen}
        onClose={setHistoryModalOpen}
        title={FH.file_history_title}
        size="lg"
      >
        {activeFileName && <p className="text-base font-semibold text-gray-900 break-all mb-2">{activeFileName}</p>}
        {previewLoading ? (
          <p className="text-gray-500 py-8 text-center">{FH.loading_preview}</p>
        ) : (
          renderMediaPreview({ imageUrls: previewImageUrls, videoUrl: previewVideoUrl, audioUrl: previewAudioUrl, alt: activeFileName, noPreviewText: FH.unable_to_load_file_preview })
        )}
        <FileHistory entries={activeFileUrl ? (documentHistory[activeFileUrl] ?? []) : []} />
      </Modal>

      <Modal
        open={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        title={FH.rename_prefix}
        size="md"
      >
        <div className="flex flex-col gap-4">
          {previewLoading ? (
            <p className="text-gray-500 py-8 text-center">{FH.loading_preview}</p>
          ) : (
            renderMediaPreview({ imageUrls: previewImageUrls, videoUrl: previewVideoUrl, audioUrl: previewAudioUrl, alt: activeFileName, noPreviewText: FH.unable_to_load_file_preview })
          )}
          <Input
            label={FH.new_filename}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setRenameModalOpen(false)}>
              {L.apply.cancel}
            </Button>
            <Button
              isDisabled={!renameValue.trim()}
              onClick={async () => {
                if (!activeFileUrl || !renameValue.trim()) return;
                const result = await postRenameDocument({ url: activeFileUrl, display_name: renameValue.trim(), applicant: props.applicant });
                if (!result || 'error' in result) return;
                setRenameModalOpen(false);
                await fetchDocumentsAndHistory();
              }}
            >
              {FH.rename}
            </Button>
          </div>
        </div>
      </Modal>

      <AttachmentActionModal
        kind="copy_to"
        open={copyToModalOpen}
        onClose={() => setCopyToModalOpen(false)}
        applicant={props.applicant}
        fileUrl={activeFileUrl ?? ''}
        displayName={activeFileName}
        documentTargetFields={documentTargetFields}
        fieldDocumentCounts={fieldDocumentCounts}
        sourceTargetField={copyToSourceField ?? undefined}
        initialRemoveFromSource={copyToInitialMove}
        onComplete={fetchDocumentsAndHistory}
      />

      <AttachmentActionModal
        kind="remove"
        open={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        applicant={props.applicant}
        targetField={removeTargetField ?? ''}
        fileUrl={activeFileUrl ?? ''}
        displayName={activeFileName}
        onComplete={fetchDocumentsAndHistory}
      />
    </div>
  );
}
